import { useCallback, useRef, useState } from 'react';
import { ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import { selectLoader } from '../../../reducers/documentation/documentationReducer';
import { SelectedDependencyWithClearHistoryProps } from '../dependencies/types';
import { selectSaltoObjectTypes } from '../../../reducers/global/parserReducer';
import { useUniversalSearchContext } from './UniversalSearchContext';
import { useDocumentationApiFacade } from '../../../apis/facades/useDocumentationApiFacade';
import { telemetry } from '../../../telemetry';
import { SearchResponse, SearchType } from './types';
import { uniqueId } from '../../../lib/uniqueId';
import flatten from 'lodash/flatten';

interface UseUniversalSearchProps {
  crmOrgId: string;
  onItemClick?: (
    props: SelectedDependencyWithClearHistoryProps,
    results?: SearchResponse,
  ) => Promise<void>;
  onClearClick?: () => void;
  onBiEventSearchStarted?: (props: { searchQuery: string; searchType: string }) => void;
  onBiEventSearchResultsPresented?: (props: {
    searchQuery: string;
    numberOfResults: number;
  }) => void;
}

export const useUniversalSearch = ({
  crmOrgId,
  onItemClick,
  onClearClick,
  onBiEventSearchStarted,
  onBiEventSearchResultsPresented,
}: UseUniversalSearchProps) => {
  // Get state and setters from context
  const {
    searchTxt,
    setSearchTxt,
    isResultsLoading,
    setIsResultsLoading,
    isUniversalSearchListOpen,
    setIsUniversalSearchListOpen,
    setResults,
    setFilterKey,
    selectedSearchType,
    setSelectedSearchType,
    filterOptions,
    searchTypeOptions,
    filteredResults,
    selectedFilterValue,
  } = useUniversalSearchContext();
  const [tempSearchTxt, setTempSearchTxt] = useState(searchTxt);

  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const showLoader = useSelector(selectLoader(crmOrgId));

  const { get_searchNameAndLabel, get_searchContent } = useDocumentationApiFacade();

  const searchRequestIdRef = useRef<string>('');

  // Business logic methods
  const performSearch = useCallback(
    async (searchText: string, searchType: SearchType, requestId: string) => {
      if (!crmOrgId || !searchText.trim()) return;

      setIsResultsLoading(true);

      try {
        let searchResults;

        if (searchType === SearchType.content) {
          onBiEventSearchStarted?.({
            searchQuery: searchText,
            searchType: 'byContent',
          });

          searchResults = await get_searchContent({
            crmOrgId,
            searchTxt: searchText,
          });
        } else {
          onBiEventSearchStarted?.({
            searchQuery: searchText,
            searchType: 'byName',
          });

          searchResults = await get_searchNameAndLabel({
            crmOrgId,
            searchTxt: searchText,
          });
        }

        // Only update results if this is still the latest request
        if (searchRequestIdRef.current === requestId) {
          setResults(searchResults);
          setIsResultsLoading(false);
          const numberOfResults = flatten(Object.values(searchResults ?? {})).length;
          onBiEventSearchResultsPresented?.({
            searchQuery: searchText,
            numberOfResults,
          });
        }
      } catch (e) {
        if (searchRequestIdRef.current === requestId) {
          telemetry.captureError(e);
          setResults(undefined);
          setIsResultsLoading(false);
        }
      }
    },
    [
      crmOrgId,
      setIsResultsLoading,
      setResults,
      onBiEventSearchStarted,
      onBiEventSearchResultsPresented,
      get_searchContent,
      get_searchNameAndLabel,
    ],
  );

  const onClearButtonClick = useCallback(() => {
    searchRequestIdRef.current = '';
    setResults(undefined);
    setFilterKey(undefined);
    setSearchTxt('');
    setTempSearchTxt('');
    setIsUniversalSearchListOpen(false);
    onClearClick?.();
  }, [setResults, setFilterKey, setSearchTxt, setIsUniversalSearchListOpen, onClearClick]);

  const performSearchProgrammatically = useCallback(
    async (searchText: string, _selectedSearchType?: SearchType) => {
      if (!searchText.trim()) {
        onClearButtonClick();
        return;
      }

      const requestId = uniqueId();
      searchRequestIdRef.current = requestId;

      setTempSearchTxt(searchText);
      setSearchTxt(searchText);
      await performSearch(searchText, _selectedSearchType ?? selectedSearchType, requestId);
    },
    [onClearButtonClick, setSearchTxt, performSearch, selectedSearchType],
  );

  const onSearchKeydown = useCallback(
    async (event: React.KeyboardEvent<HTMLDivElement>, _selectedSearchType?: SearchType) => {
      if (event.key === 'Escape') {
        onClearButtonClick();
        return;
      }
      if (event.key === 'Enter') {
        await performSearchProgrammatically(tempSearchTxt, _selectedSearchType);
        return;
      }
    },
    [tempSearchTxt, onClearButtonClick, performSearchProgrammatically],
  );

  const onSelectSearchTypeOption = useCallback(
    async (value: string) => {
      const newSearchType = value as SearchType;
      setSelectedSearchType(newSearchType);

      if (searchTxt) {
        await performSearchProgrammatically(searchTxt, newSearchType);
      }
    },
    [searchTxt, setSelectedSearchType, performSearchProgrammatically],
  );

  const onListItemClick = useCallback(
    async (props: SelectedDependencyWithClearHistoryProps) => {
      await onItemClick?.(props, filteredResults);
    },
    [onItemClick, filteredResults],
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTempSearchTxt(event ? event.target.value : '');
    },
    [],
  );

  const onSelectedFilterItem = useCallback(
    (filterKey?: string) => {
      setFilterKey(filterKey);
    },
    [setFilterKey],
  );

  return {
    // State
    searchTxt,
    tempSearchTxt,
    isLoadingObjects: !saltoObjectTypes?.length || showLoader,
    isUniversalSearchListOpen,
    isLoadingResults: isResultsLoading,
    results: filteredResults,
    filterOptions,
    selectedFilterValue,
    searchTypeOptions,
    selectedSearchType,

    // Actions
    onSearchKeydown,
    performSearchProgrammatically,
    onClearButtonClick,
    onSearchChange,
    onSelectedFilterItem,
    onListItemClick,
    onSelectSearchTypeOption,
    setIsUniversalSearchListOpen,
  };
};
